/**
 * Onglet Indicateurs de la vue d'ensemble (plan F7) : une carte par projet, une synthèse
 * globale. Un indicateur vide affiche « — » avec sa raison, jamais `0 %`. La météo porte
 * toujours un texte, en plus du glyphe coloré (couleur jamais seule porteuse de sens).
 */

import { useTranslation } from 'react-i18next';
import { Empty, Eyebrow } from '../../components/common.jsx';
import { weatherClass, weatherKey } from '../../lib/weather.js';
import { CELLS_PLANCHE, CELL_PLANCHE, TAG } from '../../ui/theme.js';

function formatPercent(locale, value) {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
    value,
  );
}

function IndicatorCard({ project, indicators, t, locale }) {
  const throughputMax = Math.max(1, ...indicators.throughput.map((week) => week.count));
  const throughputTotal = indicators.throughput.reduce((sum, week) => sum + week.count, 0);

  return (
    <div className={`${CELL_PLANCHE} flex flex-col gap-2 bg-paper p-4`}>
      <span className="ticks" aria-hidden="true" />
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs uppercase tracking-label text-ink-faint">
          {project?.key ?? t('overview.indicators.globalTitle')}
        </span>
        <span className={`${TAG} border-line ${weatherClass(indicators.weather)}`}>
          <span aria-hidden="true">●</span>
          {t(weatherKey(indicators.weather))}
        </span>
      </div>
      {project && <p className="text-sm font-semibold text-ink">{project.name}</p>}

      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-ink-faint">{t('overview.indicators.onTime')}</dt>
        <dd className="text-base font-semibold tabular-nums text-ink">
          {indicators.onTime
            ? formatPercent(locale, indicators.onTime.value)
            : `— (${t('overview.indicators.emptyOnTime')})`}
        </dd>

        <dt className="text-ink-faint">{t('overview.indicators.overdue')}</dt>
        <dd className="text-base font-semibold tabular-nums text-ink">
          {t('overview.indicators.overdueRatio', {
            count: indicators.overdue.count,
            open: indicators.overdue.open,
          })}
        </dd>

        <dt className="text-ink-faint">{t('overview.indicators.scheduleGap')}</dt>
        <dd className="text-base font-semibold tabular-nums text-ink">
          {indicators.scheduleGap
            ? t('overview.indicators.schedulePoints', { value: indicators.scheduleGap.value })
            : `— (${t('overview.indicators.emptySchedule')})`}
        </dd>

        <dt className="text-ink-faint">{t('overview.indicators.estimateReliability')}</dt>
        <dd className="text-base font-semibold tabular-nums text-ink">
          {indicators.estimateReliability
            ? t('overview.indicators.reliabilityFactor', {
                value: new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(
                  indicators.estimateReliability.value,
                ),
              })
            : `— (${t('overview.indicators.emptyReliability')})`}
        </dd>
      </dl>

      <div>
        <p className="font-mono text-[10px] uppercase tracking-label text-ink-faint">
          {t('overview.indicators.throughput')}
        </p>
        <svg role="img" width="128" height="32" className="mt-1">
          <title>{t('overview.indicators.throughput')}</title>
          {indicators.throughput.map((week, index) => {
            const height = Math.round((week.count / throughputMax) * 28) || 1;
            return (
              <rect
                key={week.week}
                x={index * 16}
                y={32 - height}
                width={12}
                height={height}
                className="fill-accent-trait"
              >
                <title>
                  {t('overview.indicators.throughputBarAria', {
                    week: week.week,
                    count: week.count,
                  })}
                </title>
              </rect>
            );
          })}
        </svg>
        <p className="font-mono text-[11px] tabular-nums text-ink-soft">
          {t('overview.indicators.throughputTotal', { count: throughputTotal })}
        </p>
      </div>
    </div>
  );
}

export function OverviewIndicators({ data }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];

  const projects = data?.projects ?? [];
  const byProject = data?.indicators?.byProject ?? [];
  const global = data?.indicators?.global;

  if (!data) return null;
  if (projects.length === 0) {
    return <Empty>{t('overview.empty.projects')}</Empty>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Eyebrow>{t('overview.indicators.globalTitle')}</Eyebrow>
        {global && (
          <div className={`${CELLS_PLANCHE} grid-cols-1`}>
            <IndicatorCard project={null} indicators={global} t={t} locale={locale} />
          </div>
        )}
      </div>

      <div className={`${CELLS_PLANCHE} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`}>
        {byProject.map((indicators) => (
          <IndicatorCard
            key={indicators.projectId}
            project={projects.find((project) => project.id === indicators.projectId)}
            indicators={indicators}
            t={t}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
}
